import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { api, ApiError, CoachUsage, TrainingGoal } from "@/api";
import { useUI } from "@/lib/theme";
import { ChatBubble, WestyMark } from "../ui";
import CoachMemory from "../CoachMemory";

interface ChatMsg {
  role: "user" | "assistant";
  content: string;
}

interface GoalMeta {
  targetTime: string;
}

function inline(text: string, keyBase: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={`${keyBase}-${i}`}>{p.slice(2, -2)}</strong>
    ) : (
      <span key={`${keyBase}-${i}`}>{p}</span>
    ),
  );
}

function renderLine(line: string, key: number): ReactNode {
  const h = /^(#{1,3})\s+(.*)$/.exec(line);
  if (h) return <p key={key} className="mb-1 mt-2 font-bold text-fg">{inline(h[2], `h${key}`)}</p>;
  const li = /^\s*[-*]\s+(.*)$/.exec(line);
  if (li) return <li key={key} className="ml-4 list-disc">{inline(li[1], `li${key}`)}</li>;
  const q = /^\s*>\s?(.*)$/.exec(line);
  if (q)
    return (
      <p key={key} className="border-l-2 border-line pl-3 text-xs italic text-muted">
        {inline(q[1], `q${key}`)}
      </p>
    );
  if (!line.trim()) return <div key={key} className="h-2" />;
  return <p key={key}>{inline(line, `p${key}`)}</p>;
}

function goalMeta(g: TrainingGoal | null | undefined): GoalMeta {
  if (!g?.notes) return { targetTime: "" };
  try {
    const parsed = JSON.parse(g.notes) as { targetTime?: unknown };
    return { targetTime: typeof parsed.targetTime === "string" ? parsed.targetTime : "" };
  } catch {
    return { targetTime: "" };
  }
}

function encodeGoalMeta(meta: GoalMeta): string | null {
  const targetTime = meta.targetTime.trim();
  return targetTime ? JSON.stringify({ targetTime }) : null;
}

function formatTokens(value: number, lang: "es" | "en"): string {
  return new Intl.NumberFormat(lang === "en" ? "en-US" : "es-ES", { maximumFractionDigits: 0 }).format(value);
}

export default function Coach({ initialPrompt }: { initialPrompt?: string | null }) {
  const { t, lang } = useUI();
  const [threadId, setThreadId] = useState<string | null>(null);
  const [history, setHistory] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [goals, setGoals] = useState<TrainingGoal[]>([]);
  const [goalTitle, setGoalTitle] = useState("");
  const [goalDate, setGoalDate] = useState("");
  const [goalTime, setGoalTime] = useState("");
  const [editingGoal, setEditingGoal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [goalNotice, setGoalNotice] = useState<string | null>(null);
  const [usage, setUsage] = useState<CoachUsage | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const activeGoal = useMemo(() => goals.find((g) => g.status === "active") ?? null, [goals]);
  const activeGoalMeta = goalMeta(activeGoal);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [history, loading]);

  useEffect(() => {
    if (initialPrompt) setInput(initialPrompt);
  }, [initialPrompt]);

  async function reloadPlanning() {
    const g = await api.coachGoals();
    setGoals(g.goals);
  }

  useEffect(() => {
    void reloadPlanning().catch(() => {});
    void api
      .coachUsage()
      .then((res) => setUsage(res.usage))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!activeGoal || editingGoal) return;
    setGoalTitle(activeGoal.title);
    setGoalDate(activeGoal.target_date ?? "");
    setGoalTime(goalMeta(activeGoal).targetTime);
  }, [activeGoal, editingGoal]);

  const suggestions = [t("coach_s1"), t("coach_s2"), t("coach_s3")];

  async function send(text: string) {
    const q = text.trim();
    if (!q || loading) return;
    setInput("");
    setError(null);
    setHistory((h) => [...h, { role: "user", content: q }]);
    setLoading(true);
    try {
      const res = await api.coach({ message: q, lang, threadId });
      setThreadId(res.threadId);
      setUsage(res.usage);
      setHistory((h) => [...h, { role: "assistant", content: res.reply }]);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) setError(t("coach_rate"));
      else setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  function flashGoalNotice(msg: string) {
    setGoalNotice(msg);
    window.setTimeout(() => setGoalNotice((cur) => (cur === msg ? null : cur)), 2500);
  }

  async function saveGoal(e: FormEvent) {
    e.preventDefault();
    if (!goalTitle.trim() || saving) return;
    setSaving(true);
    try {
      const body = {
        title: goalTitle,
        targetDate: goalDate || null,
        priority: "normal" as const,
        notes: encodeGoalMeta({ targetTime: goalTime }),
      };
      if (activeGoal) await api.updateCoachGoal(activeGoal.id, body);
      else await api.createCoachGoal(body);
      setEditingGoal(false);
      await reloadPlanning();
      flashGoalNotice(t("coach_goal_saved"));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  async function archiveGoal() {
    if (!activeGoal || saving) return;
    setSaving(true);
    try {
      await api.updateCoachGoal(activeGoal.id, { status: "archived" });
      setEditingGoal(false);
      await reloadPlanning();
      flashGoalNotice(t("coach_goal_archived"));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  async function removeGoal() {
    if (!activeGoal || saving) return;
    if (!window.confirm(t("coach_delete_goal_confirm"))) return;
    setSaving(true);
    try {
      await api.deleteCoachGoal(activeGoal.id);
      setEditingGoal(false);
      setGoalTitle("");
      setGoalDate("");
      setGoalTime("");
      await reloadPlanning();
      flashGoalNotice(t("coach_goal_deleted"));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  function startGoalEdit() {
    setGoalTitle(activeGoal?.title ?? "");
    setGoalDate(activeGoal?.target_date ?? "");
    setGoalTime(goalMeta(activeGoal).targetTime);
    setEditingGoal(true);
  }

  return (
    <section className="rounded-xl border border-line bg-panel/70 p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="inline-block h-2.5 w-2.5 rounded-full bg-accent" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted">{t("coach_title")}</h2>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {usage && (
            <span
              title={t("coach_credit_hint")}
              className="rounded-md border border-accent/30 bg-accent/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent"
            >
              {t("coach_credit", {
                remaining: formatTokens(usage.remainingTokens, lang),
                limit: formatTokens(usage.limitTokens, lang),
              })}
            </span>
          )}
          <span className="rounded-md border border-accent/30 bg-accent/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent">
            beta
          </span>
        </div>
      </div>

      <div className="mb-3 rounded-xl border border-accent/30 bg-accent/10 p-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-xs font-bold uppercase tracking-wider text-accent">{t("coach_pinned_goal")}</div>
            {activeGoal && !editingGoal ? (
              <>
                <div className="mt-0.5 text-base font-bold text-fg">{activeGoal.title}</div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted">
                  <span>{activeGoal.target_date ? t("coach_goal_date", { date: activeGoal.target_date }) : t("coach_no_date")}</span>
                  <span>
                    {activeGoalMeta.targetTime
                      ? t("coach_goal_time", { time: activeGoalMeta.targetTime })
                      : t("coach_no_time")}
                  </span>
                </div>
              </>
            ) : (
              <p className="mt-1 text-sm text-muted">{t("coach_goal_empty_desc")}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {goalNotice && (
              <span className="rounded-md border border-accent/30 bg-accent/10 px-2 py-0.5 text-[11px] font-semibold text-accent">
                ✓ {goalNotice}
              </span>
            )}
            {activeGoal && !editingGoal && (
              <>
                <button
                  onClick={startGoalEdit}
                  className="rounded-lg border border-accent/40 bg-panel px-3 py-1.5 text-xs font-semibold text-accent transition hover:bg-accent/10"
                >
                  {t("coach_edit_goal")}
                </button>
                <button
                  onClick={archiveGoal}
                  disabled={saving}
                  className="rounded-lg border border-muted/40 bg-panel px-3 py-1.5 text-xs font-semibold text-muted transition hover:border-muted/70 hover:text-fg disabled:opacity-40"
                >
                  {t("coach_archive_goal")}
                </button>
                <button
                  onClick={removeGoal}
                  disabled={saving}
                  className="rounded-lg border border-red-500/40 bg-panel px-3 py-1.5 text-xs font-semibold text-red-500 transition hover:bg-red-500/10 disabled:opacity-40"
                >
                  {t("coach_delete_goal")}
                </button>
              </>
            )}
          </div>
        </div>

        {(!activeGoal || editingGoal) && (
          <form onSubmit={saveGoal} className="mt-3 grid gap-2 md:grid-cols-[minmax(0,1fr)_9rem_8rem_auto]">
            <input
              value={goalTitle}
              onChange={(e) => setGoalTitle(e.target.value)}
              placeholder={t("coach_goal_placeholder")}
              className="min-w-0 rounded-lg border border-line bg-panel px-3 py-2 text-sm outline-none focus:border-accent"
            />
            <input
              type="date"
              value={goalDate}
              onChange={(e) => setGoalDate(e.target.value)}
              className="rounded-lg border border-line bg-panel px-3 py-2 text-sm outline-none focus:border-accent"
            />
            <input
              value={goalTime}
              onChange={(e) => setGoalTime(e.target.value)}
              placeholder="1:29:59"
              className="rounded-lg border border-line bg-panel px-3 py-2 text-sm outline-none focus:border-accent"
            />
            <div className="flex gap-2">
              <button
                disabled={saving || !goalTitle.trim()}
                className="flex-1 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {activeGoal ? t("coach_save_goal") : t("coach_add_goal")}
              </button>
              {activeGoal && editingGoal && (
                <button
                  type="button"
                  onClick={() => setEditingGoal(false)}
                  disabled={saving}
                  className="rounded-lg border border-line bg-panel px-3 py-2 text-sm font-semibold text-muted transition hover:text-fg disabled:opacity-40"
                >
                  {t("coach_cancel_goal")}
                </button>
              )}
            </div>
          </form>
        )}
      </div>

      <CoachMemory />

      <div className="flex min-w-0 flex-col">
        <div
          ref={scrollRef}
          className="flex h-[38vh] min-h-[250px] max-h-[420px] flex-col gap-2 overflow-y-auto pr-1 md:h-[42vh]"
        >
          {history.length === 0 && (
            <div className="m-auto max-w-md text-center text-sm text-muted">
              <p>{t("coach_intro")}</p>
            </div>
          )}
          {history.map((m, i) => (
            <ChatBubble
              key={i}
              role={m.role === "user" ? "user" : "ai"}
              accentRgb="139 92 246"
              avatar={m.role === "assistant" ? <WestyMark className="h-7 w-7" /> : undefined}
            >
              {m.role === "assistant" ? (
                <div className="space-y-0.5">{m.content.split("\n").map((l, k) => renderLine(l, k))}</div>
              ) : (
                m.content
              )}
            </ChatBubble>
          ))}
          {loading && (
            <ChatBubble role="ai" accentRgb="139 92 246" avatar={<WestyMark className="h-7 w-7" />}>
              <span className="text-muted">{t("coach_thinking")}</span>
            </ChatBubble>
          )}
          {error && (
            <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-300">
              {t("coach_error")}: {error}
            </div>
          )}
        </div>

        {history.length === 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button
                key={s}
                onClick={() => void send(s)}
                disabled={loading}
                className="rounded-lg border border-line px-3 py-1.5 text-xs text-muted transition hover:border-accent hover:text-accent disabled:opacity-50"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void send(input);
          }}
          className="mt-2 flex items-end gap-2"
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void send(input);
              }
            }}
            rows={1}
            placeholder={t("coach_placeholder")}
            className="max-h-24 min-h-[38px] flex-1 resize-none rounded-xl border border-line bg-panel px-3 py-2 text-sm text-fg outline-none transition focus:border-accent"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-white transition enabled:hover:brightness-110 disabled:opacity-60"
          >
            {t("coach_send")}
          </button>
        </form>
      </div>

      <p className="mt-1.5 text-[11px] text-muted">{t("coach_disclaimer")}</p>
    </section>
  );
}
